import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Phone, AlertTriangle, Download, ArrowUpRight, ArrowDownRight, Minus, GitCompareArrows } from 'lucide-react';
import { toast } from 'sonner';
import { DateRangePicker } from '@/components/DateRangePicker';
import { GeoFilters } from '@/components/GeoFilters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Legend, Cell } from 'recharts';
import { formatChartLabel, formatDayMonth } from '@/utils/dateFormat';

const formatINR = (v) => {
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

const COLORS = ['#10B981', '#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{formatChartLabel(label)}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />{p.name}: {p.value?.toLocaleString('en-IN')}
        </p>
      ))}
    </div>
  );
}

function downloadCSV(rows, filename) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => {
    const v = r[k];
    return typeof v === 'string' && v.includes(',') ? `"${v}"` : v ?? '';
  }).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function ChartHeader({ title, data, filename, className }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className={`font-heading font-semibold text-sm ${className || ''}`}>{title}</h3>
      {data && data.length > 0 && (
        <button
          onClick={() => downloadCSV(data, filename)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-muted"
          data-testid={`download-${filename}`}
          title="Download CSV"
        >
          <Download size={13} /> CSV
        </button>
      )}
    </div>
  );
}

function DeltaIndicator({ current, previous, inverse }) {
  if (previous === undefined || previous === null) return null;
  const change = previous !== 0 ? ((current - previous) / previous * 100) : 0;
  const isPositive = change > 0;
  const isNeutral = Math.abs(change) < 0.5;
  // For some metrics, down is bad (visits, productive). For others, down might be contextual.
  const isGood = inverse ? !isPositive : isPositive;

  if (isNeutral) return <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground mt-1"><Minus size={10} /> No change</span>;

  return (
    <span className={`flex items-center gap-0.5 text-[10px] mt-1 ${isGood ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
      {isPositive ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
      {isPositive ? '+' : ''}{change.toFixed(1)}% vs prev
    </span>
  );
}

export default function VisitsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [compare, setCompare] = useState(false);


  useEffect(() => {
    setLoading(true);
    const params = {};
    if (zone) params.zone = zone;
    if (subzone) params.subzone = subzone;
    if (state) params.state = state;
    if (city) params.city = city;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (compare) params.compare = true;
    api.get('/visits/analytics', { params }).then(r => setData(r.data)).catch(() => toast.error('Failed to load visit analytics')).finally(() => setLoading(false));
  }, [api, zone, subzone, state, city, dateFrom, dateTo, compare]);

  const k = data?.kpis || {};
  const pk = data?.prev_kpis;

  const hasFilters = zone || subzone || state || city || dateFrom || dateTo;
  const isEmpty = !data || k.total_visits === 0;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  const funnelTotal = (data.funnel || []).reduce((s, f) => s + f.value, 0);
  const funnelBars = (data.funnel || []).map(f => ({
    name: f.name,
    value: f.value,
    pct: funnelTotal > 0 ? Math.round(f.value / funnelTotal * 100) : 0,
  }));

  return (
    <div className="space-y-6" data-testid="visits-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Visits</h1><p className="text-sm text-muted-foreground mt-1">FE visit analytics — conversion, time spent, and outlet insights</p></div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <GeoFilters api={api} zone={zone} setZone={setZone} subzone={subzone} setSubzone={setSubzone}
          state={state} setState={setState} city={city} setCity={setCity} />
        <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
        <button
          onClick={() => setCompare(c => !c)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border transition-colors ${compare ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'}`}
          data-testid="compare-toggle"
        >
          <GitCompareArrows size={14} /> Compare Period
        </button>
        {compare && data?.compare_label && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded" data-testid="compare-label">{data.compare_label}</span>
        )}
      </div>

      {/* Empty State */}
      {isEmpty && (
        <div className="bg-card border border-border rounded-md p-12 text-center">
          <Phone size={40} strokeWidth={1} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading font-semibold text-lg mb-2">{hasFilters ? 'No Results' : 'No Visit Data'}</h3>
          <p className="text-sm text-muted-foreground">{hasFilters ? 'No visit data found for the selected filters. Try adjusting the date range or zone.' : 'Upload FE Visits (TC) and FE Visits (PC) files from Configuration to see analytics.'}</p>
        </div>
      )}

      {/* KPI Cards */}
      {!isEmpty && <><div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-card border border-border rounded-md p-4" data-testid="kpi-total-visits">
          <p className="kpi-label text-muted-foreground">Total Visits</p>
          <p className="text-xl font-heading font-bold mt-1">{k.total_visits?.toLocaleString('en-IN')}</p>
          {pk && <DeltaIndicator current={k.total_visits} previous={pk.total_visits} />}
        </div>
        <div className="bg-card border border-green-200 dark:border-green-900 rounded-md p-4" data-testid="kpi-productive">
          <p className="kpi-label text-green-600 dark:text-green-400">Productive</p>
          <p className="text-xl font-heading font-bold mt-1 text-green-600 dark:text-green-400">{k.productive?.toLocaleString('en-IN')}</p>
          {pk && <DeltaIndicator current={k.productive} previous={pk.productive} />}
        </div>
        <div className="bg-card border border-red-200 dark:border-red-900 rounded-md p-4" data-testid="kpi-non-productive">
          <p className="kpi-label text-red-600 dark:text-red-400">Non-Productive</p>
          <p className="text-xl font-heading font-bold mt-1 text-red-600 dark:text-red-400">{k.non_productive?.toLocaleString('en-IN')}</p>
          {pk && <DeltaIndicator current={k.non_productive} previous={pk.non_productive} inverse />}
        </div>
        <div className="bg-card border border-border rounded-md p-4" data-testid="kpi-conversion-rate">
          <p className="kpi-label text-muted-foreground">Conversion Rate</p>
          <p className="text-xl font-heading font-bold mt-1">{k.conversion_rate}%</p>
          {pk && <DeltaIndicator current={k.conversion_rate} previous={pk.conversion_rate} />}
        </div>
        <div className="bg-card border border-amber-200 dark:border-amber-900 rounded-md p-4" data-testid="kpi-pending-bills">
          <p className="kpi-label text-amber-600 dark:text-amber-400">Pending Bills</p>
          <p className="text-xl font-heading font-bold mt-1 text-amber-600 dark:text-amber-400">{k.outlets_with_pending?.toLocaleString('en-IN')} outlets</p>
          {pk && <DeltaIndicator current={k.outlets_with_pending} previous={pk.outlets_with_pending} inverse />}
        </div>
        <div className="bg-card border border-border rounded-md p-4" data-testid="kpi-pending-amount">
          <p className="kpi-label text-muted-foreground">Pending Amount</p>
          <p className="text-xl font-heading font-bold mt-1">INR {formatINR(k.pending_bills_total || 0)}</p>
          {pk && <DeltaIndicator current={k.pending_bills_total} previous={pk.pending_bills_total} inverse />}
        </div>
      </div>

      {/* Row 1: Funnel + FE Leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-5" data-testid="chart-funnel">
          <ChartHeader title="Visit Conversion Funnel" data={funnelBars} filename="visit-funnel" />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelBars} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={110} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="chart-tooltip">
                      <p className="text-xs font-heading font-semibold text-foreground mb-1">{label}</p>
                      <p className="text-xs text-muted-foreground">Count: {payload[0].value?.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-muted-foreground">Share: {funnelBars.find(f => f.name === label)?.pct || 0}%</p>
                    </div>
                  );
                }} />
                <Bar dataKey="value" name="Visits" radius={[0, 4, 4, 0]}>
                  {funnelBars.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-5" data-testid="chart-fe-top">
          <ChartHeader title="FE Conversion — Top Performers" data={data.fe_top} filename="fe-top-performers" />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.fe_top || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={120} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="rate" name="Conversion %" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 2: Time Spent + Beat Conversion */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-5" data-testid="chart-time-spent">
          <ChartHeader title="Time Spent vs Conversion Rate" data={data.time_data} filename="time-vs-conversion" />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.time_data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="bucket" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} unit="%" stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="total" name="Visits" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="rate" name="Conversion %" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-5" data-testid="chart-beat-conv">
          <ChartHeader title="Beat-wise Conversion — Top 15" data={data.beat_top} filename="beat-conversion" />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.beat_top || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="beat" tick={{ fontSize: 8 }} width={130} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="rate" name="Conversion %" fill="#1B3A8C" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Daily Trend */}
      <div className="bg-card border border-border rounded-md p-5" data-testid="chart-daily-trend">
        <ChartHeader title="Daily Visit Trend" data={data.daily_trend} filename="daily-visit-trend" />
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily_trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={formatDayMonth} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="productive" name="Productive" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
              <Area type="monotone" dataKey="non_productive" name="Non-Productive" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.4} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom performers + Non-converting outlets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(data.fe_bottom || []).length > 0 && (
          <div className="bg-card border border-border rounded-md p-5" data-testid="chart-fe-bottom">
            <ChartHeader title="FE Conversion — Needs Improvement" data={data.fe_bottom} filename="fe-bottom-performers" className="text-red-600 dark:text-red-400" />
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.fe_bottom} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" stroke="hsl(var(--muted-foreground))" />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={120} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="rate" name="Conversion %" fill="#EF4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-md p-5 overflow-auto" data-testid="table-non-converting">
          <ChartHeader
            title={<span className="flex items-center gap-2"><AlertTriangle size={14} className="text-amber-600" /> Non-Converting Outlets (3+ visits, 0 orders)</span>}
            data={data.non_converting}
            filename="non-converting-outlets"
          />
          <div className="max-h-64 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sticky top-0 bg-card">Outlet</TableHead>
                  <TableHead className="text-xs sticky top-0 bg-card">Beat</TableHead>
                  <TableHead className="text-right text-xs sticky top-0 bg-card">Visits</TableHead>
                  <TableHead className="text-right text-xs sticky top-0 bg-card">Pending</TableHead>
                  <TableHead className="text-xs sticky top-0 bg-card">Reasons</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.non_converting || []).map((o, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm font-medium">{o.outlet_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{o.beat}</TableCell>
                    <TableCell className="text-right text-sm">{o.visits}</TableCell>
                    <TableCell className="text-right text-sm">{o.pending_bills > 0 ? `INR ${o.pending_bills}` : '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{o.reasons}</TableCell>
                  </TableRow>
                ))}
                {(!data.non_converting || data.non_converting.length === 0) && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No data</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      </>}
    </div>
  );
}
